<template>
  <el-row class="sunburst-chart" v-loading="isLoading">
    <el-col class="sunburst-legend " :span="6">
      <el-row type="flex" class="col_headers" :gutter="2">
        <el-col
          class="subtitle2 bold"
          :class="{ 'justify-end': idx === 1 }"
          :span="idx === 0 ? 20 : 4"
          v-for="(col, idx) in colHeaders"
          :key="idx"
          >{{ col.label }}</el-col
        >
      </el-row>
      <el-divider></el-divider>
      <el-row type="flex" :gutter="2">
        <el-col class="subtitle1" :span="16">Network</el-col>
        <el-col class="subtitle2 justify-end" :span="8">{{ getEventTypePercent('network') }}</el-col>
      </el-row>
      <el-divider></el-divider>
      <el-row type="flex" :gutter="2">
        <el-col class="subtitle1" :span="16">Process</el-col>
        <el-col class="subtitle2 justify-end" :span="8">{{ getEventTypePercent('process') }} </el-col>
      </el-row>
      <el-divider></el-divider>
      <el-row type="flex" :gutter="2">
        <el-col class="subtitle1" :span="16">File</el-col>
        <el-col class="subtitle2 justify-end" :span="8">{{ getEventTypePercent('file') }} </el-col>
      </el-row>
      <el-divider></el-divider>
      <el-row type="flex" :gutter="2">
        <el-col class="subtitle1" :span="16">User</el-col>
        <el-col class="subtitle2 justify-end" :span="8">{{ getEventTypePercent('user') }} </el-col>
      </el-row>
      <el-divider></el-divider>
    </el-col>
    <el-col class="sunburst-alignments" :id="`sunburst-chart-${agentIdLabel}`" :span="18"></el-col>
  </el-row>
</template>

<script>
import { mapGetters } from 'vuex'

import { chartGlobalLabelDefaults } from '@/constants/chart-global-styles.constants'
import { HOST_EVENT_COMPOSITION_REQ } from '@/store/modules/event_composition/event_composition.type'

export default {
  name: 'SunburstChart',
  props: {
    agentId: { type: String }
  },
  data () {
    return {
      graphOptions: {
        size: { w: 400, h: 400 },
        style: chartGlobalLabelDefaults.style
      },
      isLoading: false,
      eventComposition: {},
      colHeaders: [
        {
          label: 'Event Type'
        },
        {
          label: '%'
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['dailyActvitiyEventReqStatus']),
    agentIdLabel () {
      if (this.agentId) {
        return this.agentId.replace(/#|:|\./g, '-')
      }
      return ''
    }
  },
  methods: {
    getEventTypePercent (eventType) {
      function getChildEventValueRecursive (childEvent, total = 0) {
        if (_.isUndefined(childEvent)) {
          return total
        }
        if (childEvent.hasOwnProperty('children')) {
          childEvent.children.forEach(innerEvent => {
            total = getChildEventValueRecursive(innerEvent, total)
          })
        } else if (childEvent.hasOwnProperty('value')) {
          return (total += childEvent.value)
        }
        return total
      }

      if (_.isEmpty(this.eventComposition)) {
        return 0
      }
      let selEvent = _.find(this.eventComposition.children, { name: eventType.toUpperCase() })
      let selEventTotal = getChildEventValueRecursive(selEvent)
      console.log(`${eventType} : ${selEventTotal}`)
      console.log(`total : ${this.eventComposition.total}`)
      return (selEventTotal || this.eventComposition.total)
        ? ((selEventTotal / this.eventComposition.total) * 100).toFixed(2) 
        : selEventTotal.toFixed(2);
    },
    renderChart (eventCompositionData) {
      const vm = this
      const width = this.graphOptions.size.w
      const height = this.graphOptions.size.w
      const maxRadius = Math.min(width, height) / 2 - 5

      const formatNumber = vm.$d3.format(',d')

      const x = vm.$d3
        .scaleLinear()
        .range([0, 2 * Math.PI])
        .clamp(true)

      const y = vm.$d3.scaleSqrt().range([0, maxRadius])

      const color = vm.$d3.scaleOrdinal(vm.$d3.schemeTableau10)

      const partition = vm.$d3.partition()

      const arc = vm.$d3
        .arc()
        .startAngle(d => x(d.x0))
        .endAngle(d => x(d.x1))
        .innerRadius(d => Math.max(0, y(d.y0)))
        .outerRadius(d => Math.max(0, y(d.y1)))

      const middleArcLine = d => {
        const halfPi = Math.PI / 2
        const angles = [x(d.x0) - halfPi, x(d.x1) - halfPi]
        const r = Math.max(0, (y(d.y0) + y(d.y1)) / 2)

        const middleAngle = (angles[1] + angles[0]) / 2
        const invertDirection = middleAngle > 0 && middleAngle < Math.PI // On lower quadrants write text ccw
        if (invertDirection) {
          angles.reverse()
        }

        const path = vm.$d3.path()
        path.arc(0, 0, r, angles[0], angles[1], invertDirection)
        return path.toString()
      }

      const textFits = d => {
        const CHAR_SPACE = 6

        const deltaAngle = x(d.x1) - x(d.x0)
        const r = Math.max(0, (y(d.y0) + y(d.y1)) / 2)
        const perimeter = r * deltaAngle

        return d.data.name.length * CHAR_SPACE < perimeter
      }

      const root = vm.$d3.hierarchy(eventCompositionData)
      root.sum(d => d.value)

      const svg = vm.$d3
        .select(`#sunburst-chart-${this.agentIdLabel}`)
        .append('svg')
        .style('width', width)
        .style('height', width)
        .style('margin-left', 'auto')
        .style('margin-right', 'auto')
        .style('display', 'block')
        .attr('viewBox', `${-width / 2} ${-width / 2} ${width} ${width}`)
        .attr('font-family', this.graphOptions.style.fontFamily)
        .on('click', () => focusOn()) // Reset zoom on canvas click

      const g = svg.append('g')
      const slice = g
        .selectAll('g.slice')
        .data(
          partition(root)
            .descendants()
            .slice(1)
        )
        .enter()
        .append('g')
        .attr('class', 'slice')
        .style('cursor', 'pointer')
        .on('click', d => {
          vm.$d3.event.stopPropagation()
          focusOn(d)
        })
      slice.append('title').text(d => d.data.name + '\n' + formatNumber(d.value))
      slice
        .append('path')
        .attr('class', 'main-arc')
        .attr('id', (d, i) => 'main-arc_' + i) // Unique id for each slice
        .style('fill', d => color((d.children ? d : d.parent).data.name))
        .style('stroke', '#ffffff')
        .attr('d', arc)

      const labels = svg.append('g')
      const sliceText = labels
        .selectAll('.arc-text')
        .data(
          partition(root)
            .descendants()
            .slice(1)
        )
        .enter()
        .append('text')
        .attr('display', d => (textFits(d) ? null : 'none'))
        .attr('class', 'arc-text')
        .attr('x', (d, i) => labelStartPositionX(d, i))
        .attr('dy', 18)
        .append('textPath')
        .attr('class', 'arc-textpath')
        .attr('xlink:href', (d, i) => {
          return '#main-arc_' + i
        })
        .text(d => d.data.name)

      const parentSlice = svg
        .append('circle')
        .datum(root)
        .attr('r', width / 8)
        .attr('fill', 'white')
        .attr('pointer-events', 'all')
        .style('cursor', 'pointer')
        .on('click', d => {
          vm.$d3.event.stopPropagation()
          focusOn(d)
        })

      function labelStartPositionX (d, i) {
        try {
          const el = svg.select(`#main-arc_${i}`)
          const elDimension = el.node().getBBox()
          if (elDimension.width > 80) {
            return `${elDimension.width / 4}`
          }
          return 5
        } catch (error) {
          return 5
        }
      }

      function focusOn (d = { x0: 0, x1: 1, y0: 0, y1: 1 }) {
        // Reset to top-level if no data point specified

        const transition = svg
          .transition()
          .duration(750)
          .tween('scale', () => {
            const xd = vm.$d3.interpolate(x.domain(), [d.x0, d.x1])
            const yd = vm.$d3.interpolate(y.domain(), [d.y0, 1])
            return t => {
              x.domain(xd(t))
              y.domain(yd(t))
            }
          })

        transition.selectAll('path.main-arc').attrTween('d', d => () => arc(d))
        transition.selectAll('text.arc-text').attrTween('display', d => () => (textFits(d) ? null : 'none'))

        moveStackToFront(d)
        function moveStackToFront (elD) {
          svg
            .selectAll('.slice')
            .filter(d => d === elD)
            .each(function (d) {
              this.parentNode.appendChild(this)
              if (d.parent) {
                moveStackToFront(d.parent)
              }
            })
        }
      }
    },
    setDimensions (animate = true) {
      this.graphOptions.style.marginTop = 0
      if (
        this.$el.parentElement.clientWidth <= this.$el.parentElement.clientHeight ||
        this.$el.parentElement.clientHeight === 0
      ) {
        this.graphOptions.size.w = `${this.$el.parentElement.clientWidth}`
        if (this.$el.parentElement.clientHeight !== 0) {
          this.graphOptions.style.marginTop =
            (this.$el.parentElement.clientHeight - this.$el.parentElement.clientWidth - 15) / 2
        }
      } else {
        this.graphOptions.size.w = `${this.$el.parentElement.clientHeight}`
      }
      this.graphOptions.size.h = this.graphOptions.size.w
    }
  },
  mounted () {
    this.debounceResize = _.debounce(this.setDimensions, 500)
    // window resize event
    window.addEventListener('resize', this.debounceResize)
  },
  created () {
    if (!_.isEmpty(this.agentId)) {
      this.isLoading = true
      Promise.all([this.$store.dispatch(HOST_EVENT_COMPOSITION_REQ, { agentId: this.agentId })]).then(
        results => {
          this.isLoading = false
          this.eventComposition = results[0]
          this.$nextTick(() => {
            this.setDimensions(false)
            this.renderChart(this.eventComposition)
          })
        },
        errors => {
          console.log(errors)
        }
      )
    }
  },
  beforeDestroy () {
    // remove event listeners when exiting view
    let svg = this.$d3.select(`#sunburst-chart-${this.agentIdLabel}`)
    svg.selectAll('*').remove()

    window.removeEventListener('resize', this.debounceResize)
  }
}
</script>

<style>
@import 'assets/styles/_variables.css';
</style>

<style scoped>
.sunburst-chart {
  height: 100%;
  width: 100%;
  display:flex;
}

.sunburst-legend {
  margin-top: 230px;

  .col_headers {
    margin-bottom: 5px;
  }

  .el-divider--horizontal {
    margin: -5px 0px;
  }
}

.justify-end {
  text-align: right;
}

>>> .arc-text {
  font-size: 0.75em;
}

.sunburst-alignments {
  margin-left:66px;
  width: 55%;
  display:flex;
}
</style>



// WEBPACK FOOTER //
// src/components/chart/SunburstChart.vue